'use client';

import { useFormHelper } from '@plura/client/utils';
import { cn } from '@plura/shared/utils';
import React, { forwardRef, useState } from 'react';
import './website-input.css';

type WebsiteInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'value' | 'type'
> & {
  id: string;
  error?: string;
  className?: string;
};

const PROTOCOL_RE = /^(https?):\/\//i;

/**
 * URL input with a protocol prefix chip. The visitor only types the domain —
 * the stored form value is always a full, normalized URL (protocol included).
 * Pasting a full URL folds its protocol into the chip; clicking the chip
 * toggles https/http.
 */
const WebsiteInput = forwardRef<HTMLInputElement, WebsiteInputProps>((props, ref) => {
  const { className, placeholder, ...rest } = props;
  const { onChange: helperOnChange, errorMessage, value } = useFormHelper(rest);
  const formValue = typeof value === 'string' ? value : '';

  const [protocol, setProtocol] = useState<'https://' | 'http://'>(() =>
    formValue.toLowerCase().startsWith('http://') ? 'http://' : 'https://',
  );
  const displayed = formValue.replace(PROTOCOL_RE, '');

  const commit = (text: string, proto: string) => {
    helperOnChange({
      currentTarget: { type: 'text', value: text ? `${proto}${text}` : '' },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // URLs never contain whitespace; stripping it makes paste forgiving.
    let text = e.target.value.replace(/\s+/g, '');
    let proto = protocol;
    const match = text.match(PROTOCOL_RE);
    if (match) {
      proto = `${match[1].toLowerCase()}://` as typeof protocol;
      text = text.replace(PROTOCOL_RE, '');
      setProtocol(proto);
    }
    commit(text, proto);
  };

  const toggleProtocol = () => {
    const next = protocol === 'https://' ? 'http://' : 'https://';
    setProtocol(next);
    commit(displayed, next);
  };

  return (
    <div className={cn('wsi-root', className)}>
      <div className="wsi-control">
        <button
          type="button"
          className="wsi-protocol"
          onClick={toggleProtocol}
          tabIndex={-1}
          title="Toggle https/http"
          aria-label={`Protocol: ${protocol}. Click to toggle.`}
        >
          {protocol}
        </button>
        <input
          {...rest}
          ref={ref}
          type="text"
          inputMode="url"
          autoComplete="url"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder={placeholder ?? 'example.com'}
          className="wsi-input"
          value={displayed}
          onChange={handleChange}
        />
      </div>
      {errorMessage && <p className="wsi-error">{errorMessage}</p>}
    </div>
  );
});

WebsiteInput.displayName = 'WebsiteInput';

export default WebsiteInput;
